import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCookieConsent, updateCookieConsent, isCookieConsentExpired, COOKIE_TYPES } from '../utils/cookieManager';

const { FiCookie, FiSettings, FiCheck, FiX, FiShield, FiEye, FiTrendingUp, FiShare2 } = FiIcons;

const CookieConsent = ({ showSettingsButton = false }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    const isExpired = isCookieConsentExpired();
    
    if (!consent || isExpired) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      // Load saved preferences
      setCookiePreferences(prev => ({ ...prev, ...consent }));
      console.log('Loaded cookie preferences:', consent);
    }
  }, []);

  const handleAcceptAll = () => {
    const allEnabled = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    console.log('Accepting all cookies:', allEnabled);
    setCookiePreferences(allEnabled);
    updateCookieConsent(allEnabled);
    setShowBanner(false);
    
    // Show success message
    showNotification('All cookies accepted! Your preferences have been saved.');
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    console.log('Rejecting optional cookies:', onlyNecessary);
    setCookiePreferences(onlyNecessary);
    updateCookieConsent(onlyNecessary);
    setShowBanner(false);
    
    // Show success message
    showNotification('Optional cookies rejected. Only necessary cookies are enabled.');
  };

  const handleSavePreferences = () => {
    console.log('Saving custom preferences:', cookiePreferences);
    updateCookieConsent(cookiePreferences);
    setShowBanner(false);
    setShowSettings(false);
    
    // Show success message
    const enabledCount = Object.values(cookiePreferences).filter(Boolean).length;
    showNotification(`Cookie preferences saved! ${enabledCount} cookie types enabled.`);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setCookiePreferences(prev => {
      const newPrefs = { ...prev, [type]: !prev[type] };
      console.log(`Cookie preference changed: ${type}=${newPrefs[type]}`);
      return newPrefs;
    });
  };

  const showNotification = (message) => {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300';
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
      notification.style.opacity = '1';
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Necessary Cookies',
      icon: FiShield,
      description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility.',
      examples: 'Authentication, session management, security tokens',
      required: true
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      icon: FiSettings,
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      examples: 'Language preferences, theme settings, form data'
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: FiTrendingUp,
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: 'Google Analytics, page views, user behavior'
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      icon: FiShare2,
      description: 'These cookies are used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.',
      examples: 'Social media tracking, advertising networks, remarketing'
    }
  ];

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-start space-x-4 flex-1">
                  <SafeIcon icon={FiCookie} className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      We use cookies to enhance your experience
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We use cookies and similar technologies to provide, protect, and improve our services. By continuing to use our site, you consent to our use of cookies.
                      <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline ml-1">
                        Learn more in our Privacy Policy
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 lg:ml-6">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    <SafeIcon icon={FiSettings} className="h-4 w-4 inline mr-2" />
                    Customize
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCookie} className="h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We use different types of cookies to optimize your experience on our website. You can choose which categories of cookies you want to allow. Please note that disabling some cookies may affect website functionality.
                </p>

                <div className="space-y-6">
                  {cookieCategories.map((category) => (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-3">
                          <SafeIcon icon={category.icon} className="h-5 w-5 text-blue-600 mt-1" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {category.name}
                              {category.required && (
                                <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  Required
                                </span>
                              )}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-3">
                              {category.description}
                            </p>
                            <p className="text-gray-500 text-xs">
                              <strong>Examples:</strong> {category.examples}
                            </p>
                          </div>
                        </div>
                        <div className="ml-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cookiePreferences[category.id]}
                              onChange={() => handlePreferenceChange(category.id)}
                              disabled={category.required}
                              className="sr-only peer"
                            />
                            {/* Enhanced Toggle with Clear Colors */}
                            <div
                              className={`
                                relative w-12 h-6 rounded-full transition-all duration-300 ease-in-out
                                ${cookiePreferences[category.id] 
                                  ? 'bg-green-500 shadow-lg shadow-green-200' 
                                  : 'bg-gray-300'
                                }
                                ${category.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                                hover:shadow-md
                              `}
                            >
                              {/* Toggle Circle */}
                              <div
                                className={`
                                  absolute top-0.5 left-0.5 bg-white border border-gray-300 rounded-full h-5 w-5 
                                  transition-all duration-300 ease-in-out shadow-sm
                                  ${cookiePreferences[category.id] 
                                    ? 'translate-x-6 border-green-300 shadow-md' 
                                    : 'translate-x-0 border-gray-300'
                                  }
                                `}
                              >
                                {/* Icon inside toggle */}
                                <div className="flex items-center justify-center w-full h-full">
                                  {cookiePreferences[category.id] ? (
                                    <SafeIcon icon={FiCheck} className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <SafeIcon icon={FiX} className="h-3 w-3 text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Current Status */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl"
                >
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiEye} className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Current Settings</h4>
                      <p className="text-blue-800 text-sm leading-relaxed">
                        {Object.entries(cookiePreferences).filter(([_, enabled]) => enabled).length} of {Object.keys(cookiePreferences).length} cookie types enabled. 
                        You can change these preferences at any time.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Reject All
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="h-4 w-4" />
                    <span>Save Preferences</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Link (only show if explicitly requested) */}
      {showSettingsButton && (
        <button
          onClick={() => setShowSettings(true)}
          className="text-slate-400 hover:text-white text-sm transition-colors flex items-center space-x-1"
        >
          <SafeIcon icon={FiCookie} className="h-4 w-4" />
          <span>Cookie Settings</span>
        </button>
      )}
    </>
  );
};

export default CookieConsent;